/**
 * Live Layer slice — source of truth for the right-inspector LL panel and
 * the full LL workspace. Populated from two places:
 *   • ast/pipeline/runFilesAstSync — parses `live-layer.schema.json` from the
 *     editor's loaded files and dispatches LIVE_LAYER_SCHEMA_SET.
 *   • EditorViewV2 mount — fetches taxonomy from the server once on appId
 *     change and dispatches LIVE_LAYER_TAXONOMY_SET.
 *
 * Panels never hit /api/apps/:id/live-layer/schema directly — the schema
 * lives in the user's files already, so a round-trip would be redundant AND
 * flash the empty-state card on every mode switch before the network resolves.
 */
import isEqual from 'lodash/isEqual';

import { deepMerge } from '@/features/editor/live-layer/types/live-layer.types';
import type { LLGroup, LLTaxonomy } from '@/features/editor/live-layer/types/live-layer.types';
import type { RootState } from '@/store/rootState';

/**
 * Static "which JSX element consumes an LL value" map. Computed once per AST
 * sync over `src/screens/**` by scanning `useLiveLayer(...)` calls and
 * threading the resulting variables into their JSX consumers. Keyed by the
 * babel-stamped triple `<file>:<line>:<col>` — the same values the run-engine
 * emits on `data-0/1/2` for every host element, so runtime lookup is O(1).
 */
export interface LiveLayerBinding {
  /** Schema field id in dot notation (e.g. `home.hero.headline`). */
  path: string;
  /** Enclosing group id — first segment of `path`. Focusing "home.hero"
   *  dims sibling groups in the panel. */
  groupId: string;
}

/**
 * One editing surface's unpublished state, as the surface itself sees it.
 *
 * Registered here rather than kept in component state because THREE consumers
 * need it and they sit far apart in the tree: the surface's own controls, the
 * single preview publisher (`useLiveLayerPreviewSync`), and the editor chrome
 * (the color picker's theme swatches read brand colors this way).
 *
 * The surface computes both trees — it owns its own semantics, and the two
 * mounted surfaces genuinely differ. The slice is the registry and the reason
 * there is exactly one writer to the preview.
 */
export interface LiveLayerSurfaceState {
  /**
   * What this surface contributes to the preview overlay, nested by field path.
   *
   * Empty means "contribute nothing" — for a rail panel with no draft that is
   * the correct answer, because the app's own manifest fetch is fresher than
   * the panel's published snapshot. A surface that wants its published values
   * in the preview (the LL panel, so a saved value survives clearing a draft)
   * folds them in here itself.
   */
  preview: Record<string, unknown>;
  /**
   * This surface's current values — draft over published — for editor chrome
   * that has to agree with what the surface is showing. Unlike `preview` this
   * is populated even with no draft, since a published-only value is still
   * what the user is looking at.
   */
  values: Record<string, unknown>;
}

export interface LiveLayerState {
  appId: string | null;
  schema: LLGroup[] | null;
  schemaSource: string | null;
  schemaError: string | null;
  schemaSynced: boolean;
  taxonomy: LLTaxonomy;
  taxonomyLoaded: boolean;
  /** `<file>:<line>:<col>` → binding. Populated by `syncLiveLayerBindings`. */
  bindings: Record<string, LiveLayerBinding>;
  /**
   * `path` → the `?? default` literal at the useLiveLayer call site.
   *
   * `useLiveLayer` returns undefined with nothing published, so this literal is
   * what the app actually renders in the un-overridden case. The panel resolves
   * overrides only, so without it every un-set field read blank — and a boolean
   * rendered `off` while the app rendered it `true`.
   */
  sourceDefaults: Record<string, unknown>;
  /** Keyed by publisher id — see `LiveLayerSurfaceState`. */
  surfaces: Record<string, LiveLayerSurfaceState>;
}

const liveLayerInitial: LiveLayerState = {
  appId: null,
  schema: null,
  schemaSource: null,
  schemaError: null,
  schemaSynced: false,
  taxonomy: {},
  taxonomyLoaded: false,
  bindings: {},
  sourceDefaults: {},
  surfaces: {},
};

export type LiveLayerAction =
  | {
      type: 'LIVE_LAYER_SCHEMA_SET';
      schema: LLGroup[] | null;
      source: string | null;
      error: string | null;
    }
  | { type: 'LIVE_LAYER_TAXONOMY_SET'; taxonomy: LLTaxonomy }
  | {
      type: 'LIVE_LAYER_BINDINGS_SET';
      bindings: Record<string, LiveLayerBinding>;
      sourceDefaults: Record<string, unknown>;
    }
  | { type: 'LIVE_LAYER_RESET_FOR_APP'; appId: string }
  | { type: 'LIVE_LAYER_SURFACE_SET'; source: string; surface: LiveLayerSurfaceState }
  | { type: 'LIVE_LAYER_SURFACE_CLEAR'; source: string };

export function liveLayer(state: LiveLayerState = liveLayerInitial, action: any): LiveLayerState {
  switch ((action as LiveLayerAction).type) {
    case 'LIVE_LAYER_RESET_FOR_APP': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_RESET_FOR_APP' }>;
      if (state.appId === a.appId) return state;
      return { ...liveLayerInitial, appId: a.appId };
    }
    case 'LIVE_LAYER_SCHEMA_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_SCHEMA_SET' }>;
      if (
        state.schemaSynced &&
        state.schemaSource === a.source &&
        state.schemaError === a.error &&
        isEqual(state.schema, a.schema)
      )
        return state;
      return {
        ...state,
        schema: a.schema,
        schemaSource: a.source,
        schemaError: a.error,
        schemaSynced: true,
      };
    }
    case 'LIVE_LAYER_TAXONOMY_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_TAXONOMY_SET' }>;
      if (state.taxonomyLoaded && isEqual(state.taxonomy, a.taxonomy)) return state;
      return { ...state, taxonomy: a.taxonomy, taxonomyLoaded: true };
    }
    case 'LIVE_LAYER_BINDINGS_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_BINDINGS_SET' }>;
      if (isEqual(state.bindings, a.bindings) && isEqual(state.sourceDefaults, a.sourceDefaults)) {
        return state;
      }
      return { ...state, bindings: a.bindings, sourceDefaults: a.sourceDefaults };
    }
    case 'LIVE_LAYER_SURFACE_SET': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_SURFACE_SET' }>;
      // Surfaces recompute on every render, so an equal payload must be a
      // no-op — otherwise each keystroke in an unrelated control re-renders
      // every consumer and reposts the preview.
      if (isEqual(state.surfaces[a.source], a.surface)) return state;
      return { ...state, surfaces: { ...state.surfaces, [a.source]: a.surface } };
    }
    case 'LIVE_LAYER_SURFACE_CLEAR': {
      const a = action as Extract<LiveLayerAction, { type: 'LIVE_LAYER_SURFACE_CLEAR' }>;
      if (!(a.source in state.surfaces)) return state;
      const next = { ...state.surfaces };
      delete next[a.source];
      return { ...state, surfaces: next };
    }
    default:
      return state;
  }
}

export const setLiveLayerSchema = (
  schema: LLGroup[] | null,
  source: string | null,
  error: string | null = null,
): LiveLayerAction => ({
  type: 'LIVE_LAYER_SCHEMA_SET',
  schema,
  source,
  error,
});

export const setLiveLayerTaxonomy = (taxonomy: LLTaxonomy): LiveLayerAction => ({
  type: 'LIVE_LAYER_TAXONOMY_SET',
  taxonomy,
});

export const setLiveLayerBindings = (
  bindings: Record<string, LiveLayerBinding>,
  sourceDefaults: Record<string, unknown> = {},
): LiveLayerAction => ({
  type: 'LIVE_LAYER_BINDINGS_SET',
  bindings,
  sourceDefaults,
});

export const selectLiveLayerSchema = (state: RootState) => state.liveLayer.schema;
export const selectLiveLayerSchemaError = (state: RootState) => state.liveLayer.schemaError;
export const selectLiveLayerSchemaSynced = (state: RootState) => state.liveLayer.schemaSynced;
export const selectLiveLayerTaxonomy = (state: RootState) => state.liveLayer.taxonomy;
export const selectLiveLayerBindings = (state: RootState) => state.liveLayer.bindings;
export const selectLiveLayerSourceDefaults = (state: RootState) => state.liveLayer.sourceDefaults;
export const selectLiveLayerSurfaces = (state: RootState) => state.liveLayer.surfaces;

export const setLiveLayerSurface = (
  source: string,
  surface: LiveLayerSurfaceState,
): LiveLayerAction => ({ type: 'LIVE_LAYER_SURFACE_SET', source, surface });

export const clearLiveLayerSurface = (source: string): LiveLayerAction => ({
  type: 'LIVE_LAYER_SURFACE_CLEAR',
  source,
});

/**
 * Every surface's preview overlay merged into the one tree the app is sent.
 *
 * A plain function rather than a memoised selector: this codebase has no
 * reselect, and `useSelector` here is a react-redux 6 shim where a selector
 * returning a fresh object re-renders on every store change. Callers select
 * `surfaces` (a stable reference) and run this inside `useMemo`.
 *
 * Insertion order decides a key collision. Surfaces own disjoint roots
 * (`brand`, `settings`, per-screen ids), so in practice nothing collides.
 */
export function llMergedPreview(
  surfaces: Record<string, LiveLayerSurfaceState>,
): Record<string, unknown> {
  let out: Record<string, unknown> = {};
  for (const s of Object.values(surfaces)) {
    if (s.preview && Object.keys(s.preview).length > 0) out = deepMerge(out, s.preview);
  }
  return out;
}

/** Every surface's current values merged — what the editor's own chrome reads. */
export function llMergedValues(
  surfaces: Record<string, LiveLayerSurfaceState>,
): Record<string, unknown> {
  let out: Record<string, unknown> = {};
  for (const s of Object.values(surfaces)) {
    if (s.values && Object.keys(s.values).length > 0) out = deepMerge(out, s.values);
  }
  return out;
}
